import { ReactNode } from 'react';
import { EnumSelectField, SelectField } from '@mews-commander/form';
import { FeatureFlag, SearchType, TextKey } from '@mews-commander/enums';
import { GuestInHouseReportState, Service } from '@mews-commander/data-access';
import { createOption } from '@mews-commander/ui';
import { FormSwitchField, LocalizedProperty, lt, useFeatureFlag } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { DateTimeIntervalFilterFields, FilterValueKey as CommonFilterValueKey } from '../../../common';
import { SearchHookField } from '../../../../../../components/form/hookFields';
import { ReservationsTableColumns, hideableColumns, nonLoyaltyHideableColumns } from '../common';
import { ProductFilterField, ProductFilterFieldProps } from './components';
import { FilterValueKey } from './types';

const serviceToOption = (s: Service) => createOption({ value: s.id, label: <LocalizedProperty property={s.names} /> });

interface ColumnOptionProps {
    value: ReservationsTableColumns;
    label: ReactNode;
}

const columnToOption = ({ value, label }: ColumnOptionProps) => createOption({ value, label });

const allColumnsOptions = hideableColumns.map(columnToOption);
const nonLoyaltyColumnsOptions = nonLoyaltyHideableColumns.map(columnToOption);

interface ReservationsFilterFieldsProps extends Pick<ProductFilterFieldProps, 'products'> {
    hasLoyaltyPrograms?: boolean;
    services: Array<Service>;
}

export const ReservationsFilterFields = ({ hasLoyaltyPrograms, services, products }: ReservationsFilterFieldsProps) => {
    const guestVerificationPhotoEnabled = useFeatureFlag(FeatureFlag.guestVerificationPhotoOciConfigurationEnabled);
    const reservationsTableColumns = hasLoyaltyPrograms ? allColumnsOptions : nonLoyaltyColumnsOptions;

    const visibleColumns = !guestVerificationPhotoEnabled
        ? reservationsTableColumns.filter((option) => option.value !== ReservationsTableColumns.VerificationPhoto)
        : reservationsTableColumns;

    return (
        <Stack vertical spacing="300">
            <DateTimeIntervalFilterFields
                startDateFieldName={CommonFilterValueKey.startDate}
                endDateFieldName={CommonFilterValueKey.endDate}
            />
            <SelectField
                required
                name={FilterValueKey.serviceId}
                label={lt(TextKey.Service)}
                options={services?.length ? services.map(serviceToOption) : []}
            />
            <EnumSelectField
                multiselect
                preserveMultiSelectSearch
                enumClass={GuestInHouseReportState}
                enumPrefix="GuestInHouseReportState"
                name={FilterValueKey.serviceOrderStatus}
                label={lt(TextKey.Status)}
            />
            <SelectField
                multiselect
                preserveMultiSelectSearch
                name={FilterValueKey.visibleColumns}
                label={lt('DisplayedColumnPlural')}
                options={visibleColumns}
            />
            <ProductFilterField products={products} />
            <FormSwitchField
                name={FilterValueKey.filterProductsInInterval}
                label={lt('GuestInHouseReportFiltersConsumedProducts')}
            />
            <SearchHookField
                name={FilterValueKey.customerId}
                placeholder={lt('SearchCustomerName')}
                label={lt('Customer')}
                searchTypes={[SearchType.Customer]}
            />
        </Stack>
    );
};
